import React from "react";
import Model, { Providers } from "@tripian/model";
import {
  BbTourCard,
  GygTourCard,
  RezdyProductCard,
  ToristyProductCard,
  TourCard,
  ViatorProductCard,
} from "@tripian/react";

/**
 * tour-api ürünlerini provider'a özel legacy kartlarla gösterir.
 *
 * Neden adaptör: legacy LocalExperiences sayfası her provider'ın kendi kartını
 * kullanıyor ve o kartlar provider'ın ham payload'ını bekliyor (GygTourCard →
 * Providers.Gyg.Tour vb.). tour-api ise hepsini tek bir TourSearchProduct şekline
 * indirmiş halde döndürüyor. Aşağıdaki dönüştürücüler, ilgili kartın GERÇEKTEN
 * okuduğu alanları doldurup `as unknown as` ile cast ediyor — provider tiplerinin
 * geri kalan zorunlu alanları kart tarafından hiç okunmuyor.
 *
 * Kayıp alanlar: tour-api süre/iptal koşulu/dil/offer detayı döndürmediği için
 * kartlardaki o bölümler boş kalır ya da ürünün genel `duration` değerini gösterir.
 */

/** Legacy kartı olmayan provider'lar generic kartta bu isimle gösterilir. */
const PROVIDER_NAMES: Partial<Record<number, string>> = {
  [Model.PROVIDER_ID.LIVN]: "Livn",
  [Model.PROVIDER_ID.VICTORY]: "Victory Live",
  [Model.PROVIDER_ID.CIVITATIS]: "Civitatis",
  [Model.PROVIDER_ID.GOCITY]: "GoCity",
  [Model.PROVIDER_ID.OCCASION_GENIUS]: "OccasionGenius",
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  TRY: "₺",
};

const currencySymbol = (currency?: string): string =>
  (currency && CURRENCY_SYMBOLS[currency.toUpperCase()]) || currency || "";

const coverImageUrl = (product: Model.TourSearchProduct): string =>
  product.images?.find(image => image.is_cover)?.url ||
  product.images?.[0]?.url ||
  "";

/** GygTourCardText: title, categories(parent_id'siz olanlar), overall_rating, number_of_ratings, price.values.amount */
const toGygTour = (product: Model.TourSearchProduct) =>
  ({
    title: product.title,
    // Kart yalnızca üst seviye kategorileri gösteriyor; tour-api etiketlerinin
    // hiyerarşisi olmadığı için hepsi üst seviye kabul ediliyor.
    categories: (product.tags || []).map(tag => ({
      name: tag,
      parent_id: null,
    })),
    overall_rating: product.rating || 0,
    number_of_ratings: product.ratingCount || 0,
    price: { values: { amount: product.price } },
    // GygTourCardImage ilk resmi alıp helper.getYourGuideImageFormat'tan geçiriyor;
    // o helper sadece "[format_id]" yer tutucusunu değiştirdiği için çözülmüş
    // tour-api url'lerinde etkisiz.
    pictures: (product.images || []).map(image => ({ url: image.url })),
  } as unknown as Providers.Gyg.Tour);

/** BbTourCardText: info.name, themes, offers[0].duration, offers[0].priceBreakDowns */
const toBbProduct = (product: Model.TourSearchProduct) =>
  ({
    info: { name: product.title },
    themes: product.tags || [],
    mainImage: coverImageUrl(product),
    // offers[0] koşulsuz okunuyor — boş dizi kartı çalışma anında düşürür.
    offers: [
      {
        duration: product.duration || 0,
        priceBreakDowns: [
          {
            touristType: "ADULT",
            price: { amount: product.price },
          },
        ],
      },
    ],
  } as unknown as Providers.Bb.Product);

/** ViatorProductCardText: title, flags, reviews.*, pricing.summary.fromPrice */
const toViatorProduct = (product: Model.TourSearchProduct) =>
  ({
    title: product.title,
    // Viator flag'leri sabit bir enum'dan geliyor (FREE_CANCELLATION vb.) ve kart
    // tanımadığı değeri boş string'e çeviriyor; tour-api etiketleri o kümeye
    // karşılık gelmediği için flag satırı boş bırakılıyor.
    flags: [],
    reviews: {
      totalReviews: product.ratingCount || 0,
      combinedAverageRating: product.rating || 0,
      sources: [],
    },
    pricing: { summary: { fromPrice: product.price } },
    images: [{ variants: [{ width: 400, url: coverImageUrl(product) }] }],
  } as unknown as Providers.Viator.Product);

/** RezdyProductCardText: name, tags, durationMinutes, advertisedPrice */
const toRezdyProduct = (product: Model.TourSearchProduct) =>
  ({
    name: product.title,
    tags: product.tags || [],
    durationMinutes: product.duration || undefined,
    advertisedPrice: product.price || undefined,
    images: [{ itemUrl: coverImageUrl(product) }],
  } as unknown as Providers.Rezdy.Product);

/** ToristyProductCardText: name, categories[].categoryname, starting_price.* */
const toToristyProduct = (product: Model.TourSearchProduct) =>
  ({
    name: product.title,
    categories: (product.tags || []).map(tag => ({ categoryname: tag })),
    starting_price: {
      currencySymbol: currencySymbol(product.currency),
      price: product.price,
    },
    image: { orig: coverImageUrl(product) },
  } as unknown as Providers.Toristy.Product);

interface ITourApiProductCard {
  product: Model.TourSearchProduct;
  onSelect: (product: Model.TourSearchProduct) => void;
  t: (value: Model.TranslationKey) => string;
}

const TourApiProductCard: React.FC<ITourApiProductCard> = ({
  product,
  onSelect,
  t,
}) => {
  const providerId = product.providerId as Model.PROVIDER_ID;
  const select = () => onSelect(product);

  switch (providerId) {
    case Model.PROVIDER_ID.GYG:
      return <GygTourCard tour={toGygTour(product)} bodyClicked={select} t={t} />;

    case Model.PROVIDER_ID.BOOK_BARBADOS:
      return <BbTourCard tour={toBbProduct(product)} bodyClicked={select} t={t} />;

    case Model.PROVIDER_ID.VIATOR:
      return (
        <ViatorProductCard
          product={toViatorProduct(product)}
          bodyClicked={select}
          t={t}
        />
      );

    case Model.PROVIDER_ID.REZDY:
      return (
        <RezdyProductCard
          product={toRezdyProduct(product)}
          bodyClicked={select}
        />
      );

    case Model.PROVIDER_ID.TORISTY:
      return (
        <ToristyProductCard
          product={toToristyProduct(product)}
          bodyClicked={select}
          t={t}
        />
      );

    // Victory kartı etkinliğe özel (tarih, mekan, kalan bilet) ve tour-api bu
    // alanların hiçbirini döndürmüyor; uydurma bir tarih basmak yerine Livn'de
    // olduğu gibi generic kart kullanılıyor. Diğer provider'lar (Civitatis, GoCity,
    // OccasionGenius) için de legacy kart yok.
    default:
      return (
        <TourCard
          title={product.title}
          price={product.price > 0 ? product.price : undefined}
          currency={product.currency}
          tags={product.tags || []}
          images={[coverImageUrl(product)].filter(Boolean)}
          provider={PROVIDER_NAMES[providerId] || ""}
          onBook={select}
          t={t}
        />
      );
  }
};

export default TourApiProductCard;
